<template>

<Dialog @closeModal="closeModalBtn" v-if="openCloseModal"> 
    <template v-slot:header >
        Method One: <br> Weighted Average Classification
    </template>
    <template v-slot:content >
        <p>Method 1 uses a weighted average whereby your degree is calculated based on 25% of the weighted average from your units at Level 5 (or at Level 6 if you’re on an Integrated Master’s degree) combined with 75% of the weighted average from your units at Level 6 (or Level 7 if you’re on an Integrated Master’s degree). The weighted averages are formed by combining the marks for each unit at that level according to the credit values of your units.</p>
    </template>
</Dialog>

<Dialog @closeModal="closeModalBtn"  v-if="openCloseModalMethodTwo"> 
    <template v-slot:header >
        Method Two: <br> Profiling Classification
    </template>
    <template v-slot:content >
        <p>Method 2 is determined with reference only to your marks in your Level 6 units (or Level 7 if you’re an Integrated Master’s student). To get a First, a 2:1, or a 2:2 using this method, half of your marks need to be in the same classification band, and with an overall average of no more than 2% below the threshold of that band. In other words, an overall average of 68% for this method but with 60 credits graded 70% and above will result in a First Class degree.<a href="#"></a></p>    
        <p>An overall average of 58% for this method but with 60 credits graded 60% and above will result in a 2:1 degree.</p>
        <p>An overall average of 48% for this method but with 60 credits graded 50% and above will result in a 2:2 degree.</p>
        <p>The rules for calculating a Third Class degree using this method are slightly different in that you would need to pass 120 credits with marks of at least 40%.</p>
        <p>For full information on degree classification, please visit the <a target="blank" href="https://www.mmu.ac.uk/student-life/course/assessments">Student Life Assessments</a> page.</p>     
    </template>
</Dialog>

<Dialog @closeModal="closeModalBtn" v-if="openCloseModalAlternativeMethodOne">
    <template v-slot:header >
        Method One: <br> Modified
    </template>
    <template v-slot:content >
        <p>Where you have not undertaken 120 credits at Level 5 on your degree programme (for example if you are exempted through the Policy for the Recognition of Prior Learning, or you are an outgoing exchange student), a modified form of the Method One classification shall be used, in which 120 credits at Level 6 shall contribute 100% of the Overall Weighted Average mark. The Method Two classification shall apply as in regulation.</p>
    </template>
</Dialog>

<section class="container section-large result-container mbl" :class="{ circleResultsAnimTwo: resultsClassOnOff }">

    <div class="is-flex is-align-items-center is-justify-content-center mb" :class="{ circleResultsAnim: resultsClassOnOff }">
        <q-circular-progress
        id="results" 
        show-value
        :value="circleScore"
        animation-speed="3500"
        :thickness="0.15"
        color="shade"
        track-color="grey-3"
        >
            <div class="circular-progress-text">
                <div v-if="postGradResults">
                    <h2 >{{ weightedAvClassTitle }}</h2>
                    <hr>
                    <h2>{{ weightedAvClass.toFixed(2)}}</h2>
                </div>
                <div v-else-if="weightedAvClassTitle != 'Incalculable' && weightedAvClassTitle != '120 credits required' && weightedAvClassTitle != '120 credits required for level 5 & 6'">
                    <h2>{{ weightedAvClassTitle }}</h2>
                    <hr>
                    <h2>
                        <span v-if="methodSwitch === false || lessThanOneTwentyToggle === true">{{levelSixFinalGrade.toFixed(2)}}</span>
                        <span v-else>{{ weightedAvClass.toFixed(2) }}</span>
                    </h2>
                </div>
                <div v-else>
                    <h2>{{ weightedAvClassTitle }}</h2>
                </div>
            </div>
        </q-circular-progress>
    </div> 

    <div :class="{ certificationGridColumn: lessThanOneTwentyToggle, certificationGrid: !lessThanOneTwentyToggle, circleResultsAnimTwo: resultsClassOnOff }">

            <div class="results-level-five" v-if="!lessThanOneTwentyToggle && postGradResults == false">
                <div class="level-five-average">
                    <h2>Level 5</h2>
                    <div class="stats-container">
                        <div class="stats-container-flex">
                            <h3>Level 5 average</h3>
                            <p class="cert-info-fin">{{ levelFiveFinalGrade.toFixed(2) }}</p>
                        </div>
                        <div class="stats-container-flex">
                            <h3>25%</h3>
                            <p class="cert-info-fin">{{ LvFiveTwentyFivePercent.toFixed(2) }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="results-level-six" v-if="!lessThanOneTwentyToggle && postGradResults == false">
                <div class="level-six-average">
                    <h2>Level 6</h2>
                    <div class="stats-container">
                        <div class="stats-container-flex">
                            <h3>Level 6 average</h3>
                            <p class="cert-info-fin">{{levelSixFinalGrade.toFixed(2)}}</p>
                        </div>
                        <div class="stats-container-flex">
                            <h3>75%</h3>
                            <p class="cert-info-fin">{{LvSixSeventyFivePercent.toFixed(2)}}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="results-methods"> 
                <div class="methods">
                    <h2>Methods</h2>
                    <div class="stats-container"> 
                        <div class="stats-container-flex" v-if="!lessThanOneTwentyToggle">
                            <h3>Method 1:<br> Weighted average class
                                <q-icon
                                tabindex="0"
                                @click="openCloseModal = true ; disableBackgroundScroll()"
                                @keydown.enter="openCloseModal = true ; disableBackgroundScroll()"
                                class="pulsing-help-btn"
                                size="md"
                                name="info"
                                />
                            </h3>
                            <p class="cert-info-fin">{{ weightedAvClass.toFixed(2) }}</p>
                            <p class="cert-info-fin">{{ methodOne }}</p>
                        </div>

                        <div  class="stats-container-flex" v-else>                 
                            <h3>Method 1: <br> 100% of the overall weighted average mark is level 6
                                <q-icon
                                tabindex="0"
                                @click="openCloseModalAlternativeMethodOne = true ; disableBackgroundScroll()"
                                @keydown.enter="openCloseModalAlternativeMethodOne = true; disableBackgroundScroll()"
                                class="pulsing-help-btn"
                                size="md"
                                name="info"
                                />
                            </h3>
                            <p class="cert-info-fin">{{ LvSixOneHundredPercent.toFixed(2) }}</p>
                            <p class="cert-info-fin">{{ methodOne }}</p>
                        </div>
                        <div  v-if="!postGradResults" class="stats-container-flex">
                            <h3>Method 2:<br> Profiling class
                                <q-icon
                                tabindex="0"
                                @click="openCloseModalMethodTwo = true ; disableBackgroundScroll()"
                                @keydown.enter="openCloseModalMethodTwo = true ; disableBackgroundScroll()"
                                class="pulsing-help-btn"
                                size="md"
                                name="info"
                                />
                            </h3>
                            <p class="cert-info-fin">{{ levelSixFinalGrade.toFixed(2) }}</p> 
                            <p class="cert-info-fin">{{ methodTwo }}</p>
                        </div>
                    </div>
                </div>  
            </div>

            <div class="results-final">

                <div >
                    <div>
                        <h2>Final Result</h2>

                        <div class="final-grade-layout"> 
                                <div class="stats-container-flex">
                                    <h3>Classification</h3>
                                    <p class="cert-info-fin">{{ weightedAvClassTitle }}</p>
                                </div>

                                <div class="stats-container-flex">
                                    <div v-if="postGradResults === true">
                                        <h3>Mark</h3>
                                        <p class="cert-info-fin">{{ weightedAvClass.toFixed(2) }}</p>
                                    </div>
                                    <div v-else>
                                        <h3>Mark</h3>
                                        <p v-if="methodSwitch === false || lessThanOneTwentyToggle === true" class="cert-info-fin">{{ levelSixFinalGrade.toFixed(2) }}</p>
                                        <p class="cert-info-fin" v-else >{{ weightedAvClass.toFixed(2) }}</p>
                                    </div>
                                </div>
                        </div>
                    </div>
                </div>
            </div>
    </div>
    <button title="Copy to clipboard" class="button is-large mts" @click="clipboard"><q-icon left name="content_copy"/>Copy to clipboard</button>
</section>

</template>

<script>
import { copyToClipboard } from 'quasar'
import { useQuasar } from 'quasar'
import { ref } from 'vue'
import Dialog from '@/components/Dialog.vue'
import LevelFiveAndSixVue from './calculators/LevelFiveAndSix.vue'

export default {

components: {Dialog},

props: ['resultsClassOnOff', 'weightedAvClassTitle', 'lessThanOneTwentyToggle', 'levelSixFinalGrade', 'LvSixSeventyFivePercent', 'LvSixOneHundredPercent', 'levelFiveFinalGrade', 'LvFiveTwentyFivePercent', 'weightedAvClass', 'methodOne', 'methodTwo', 'circleScore', 'methodSwitch', 'finalScore', 'postGradResults', 'component'],

setup(props, context){

const openCloseModalMethodTwo = ref(false)
const openCloseModalAlternativeMethodOne = ref(false)
const openCloseModal = ref(false)
const openClosePgCertModal = ref(false)
const openCloseMastersModal = ref(false)
const quasar = useQuasar()

quasar.notify.setDefaults({
    actions: [{ icon: 'close', color: 'white' }]
})

const closeModalBtn = () => {
    openCloseModal.value = false
    openCloseModalMethodTwo.value = false
    openCloseModalAlternativeMethodOne.value = false
    openClosePgCertModal.value = false
    openCloseMastersModal.value = false
    

    if(  openCloseModalMethodTwo.value === false || openCloseModalAlternativeMethodOne.value === false || openCloseModal.value === false || openClosePgCertModal.value === false ) {
        document.documentElement.style.overflowY = 'scroll';
        document.documentElement.style.overflowX = 'hidden';
        document.body.scroll = "yes";     
    } 
}

const disableBackgroundScroll = () => {
    if(openCloseModalMethodTwo.value === true || openCloseModalAlternativeMethodOne.value === true ||  openCloseModal.value === true || openClosePgCertModal.value === true || openCloseMastersModal.value ===  true) {
        document.documentElement.style.overflow = 'hidden';
        document.body.scroll = "no";
    } 
}

const clipboard = () => {

    let methodOneTextSwitch = ref(0)

    if(!props.lessThanOneTwentyToggle) {
        methodOneTextSwitch.value = props.weightedAvClass
    } else {
        methodOneTextSwitch.value = props.LvSixOneHundredPercent
    }
    
if(!props.postGradResults) { 

    let pasteContent = 
ref(`FINAL RESULT: ${props.weightedAvClassTitle}, ${props.finalScore.toFixed(2)};
METHOD ONE: ${props.methodOne}, ${methodOneTextSwitch.value.toFixed(2)};
METHOD TWO: ${props.methodTwo}, ${props.levelSixFinalGrade.toFixed(2)}; 
${props.lessThanOneTwentyToggle === false ? `LEVEL FIVE: (Level 5 average) ${props.levelFiveFinalGrade.toFixed(2)}, (25%) ${props.LvFiveTwentyFivePercent.toFixed(2)};` : `LEVEL SIX: (Level 6 average) ${props.levelSixFinalGrade.toFixed(2)}, (75%) ${props.LvSixSeventyFivePercent.toFixed(2)};`}
${props.lessThanOneTwentyToggle === false ? `LEVEL SIX: (Level 6 average) ${props.levelSixFinalGrade.toFixed(2)}, (75%) ${props.LvSixSeventyFivePercent.toFixed(2)};` : ``}`)

    copyToClipboard(pasteContent.value)
    .then(() => { 
        quasar.notify({
        color: 'negative', message: 'You can now paste your grade wherever you like!', icon: 'content_paste_go', position: 'bottom', classes: 'notify-copy'
        })
    })
    .catch(() => {
        quasar.notify({
        color: 'negative', message: 'Something went wrong, try again!', icon: 'error', position: 'bottom', classes: 'notify-copy'
        })
    })

} else {

let pasteContent = 
ref(`FINAL RESULT: ${props.weightedAvClassTitle}, ${props.finalScore.toFixed(2)};`)

    copyToClipboard(pasteContent.value)
    .then(() => { 
        quasar.notify({
        color: 'negative', message: 'You can now paste your grade wherever you like!', icon: 'content_paste_go', position: 'bottom', classes: 'notify-copy'
        })
    })
    .catch(() => {
        quasar.notify({
        color: 'negative', message: 'Something went wrong, try again!', icon: 'error', position: 'bottom', classes: 'notify-copy'
        })
    })
    
}

}

return {        
     clipboard,
     quasar,
     openCloseModalAlternativeMethodOne,
     openCloseModalMethodTwo,
     closeModalBtn,
     openCloseModal,
     disableBackgroundScroll,
     openClosePgCertModal, 
     openCloseMastersModal
}

}
}
</script>
